import { Injectable } from '@angular/core';
import { Auth } from '@common/auth/models/auth.model';
import { User } from '@common/users/models/user.model';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { LogoutReason } from '@portal-core/auth/enums/logout-reason.enum';
import { AuthLicenseUserData } from '@portal-core/auth/models/auth-license-user-data.model';
import { SubdomainLicense } from '@portal-core/auth/models/subdomain-license.model';
import { Observable } from 'rxjs';

/* AuthStateModel */
export class AuthStateModel {
  auth: Auth; // This is only in the data store so that it can be given to Flare after the login completes
  authLicenseUserData: AuthLicenseUserData;
  autoLoginAttempted: boolean;
  daysUntilPasswordExpires: number;
  lastActivatedRoute: string[];
  maxIdleTimeMinutes: number;
  instanceCode: string;
  subdomainLicense: SubdomainLicense;
  user: User;
  userId: string;
  userName: string;
}

/* Actions */
export class Login {
  static readonly type: string = '[Auth] Login';
  constructor(public payload: { auth: Auth, instanceCode: string }) { }
}

export class Logout {
  static readonly type: string = '[Auth] Logout';
  constructor(public payload: { reason: LogoutReason, instanceCode: string }) { }
}

export class SetUser {
  static readonly type: string = '[Auth] SetUser';
  constructor(public payload: { user: User }) { }
}

export class SetAuthLicenseUserData {
  static readonly type: string = '[Auth] SetAuthLicenseUserData';
  constructor(public payload: { authLicenseUserData: AuthLicenseUserData }) { }
}

export class SetAutoLoginAttempted {
  static readonly type: string = '[Auth] SetAutoLoginAttempted';
  constructor(public payload: { autoLoginAttempted: boolean }) { }
}

export class SetInstanceCode {
  static readonly type: string = '[Auth] SetInstanceCode';
  constructor(public payload: { instanceCode: string }) { }
}

export class SetLastActivatedRoute {
  static readonly type: string = '[Auth] SetLastActivatedRoute';
  constructor(public payload: { lastActivatedRoute: string[] }) { }
}

export class SetSubdomainLicense {
  static readonly type: string = '[Auth] SetSubdomainLicense';
  constructor(public payload: { subdomainLicense: SubdomainLicense }) { }
}

export class SwitchCentralInstance {
  static readonly type: string = '[Auth] SwitchCentralInstance';
  constructor(public payload: { instanceCode: string }) { }
}

/* AuthState */
@State<AuthStateModel>({
  name: 'authState'
})
@Injectable()
export class AuthState {
  @Selector()
  static auth(state: AuthStateModel): Auth { return state.auth; }

  @Selector()
  static authLicenseUserData(state: AuthStateModel): AuthLicenseUserData { return state.authLicenseUserData; }

  @Selector()
  static autoLoginAttempted(state: AuthStateModel): boolean { return state.autoLoginAttempted; }

  @Selector()
  static daysUntilPasswordExpires(state: AuthStateModel): number { return state.daysUntilPasswordExpires; }

  @Selector()
  static lastActivatedRoute(state: AuthStateModel): string[] { return state.lastActivatedRoute; }

  @Selector()
  static maxIdleTimeMinutes(state: AuthStateModel): number { return state.maxIdleTimeMinutes; }

  @Selector()
  static instanceCode(state: AuthStateModel): string { return state.instanceCode; }

  @Selector()
  static subdomainLicense(state: AuthStateModel): SubdomainLicense { return state.subdomainLicense; }

  @Selector()
  static user(state: AuthStateModel): User { return state.user; }

  @Selector()
  static userId(state: AuthStateModel): string { return state.userId; }

  @Selector()
  static userName(state: AuthStateModel): string { return state.userName; }

  @Action(Login)
  login(ctx: StateContext<AuthStateModel>, action: Login) {
    ctx.patchState({
      auth: action.payload.auth,
      authLicenseUserData: null,
      daysUntilPasswordExpires: action.payload.auth?.daysUntilPasswordExpires ? parseInt(action.payload.auth.daysUntilPasswordExpires, 10) : null,
      maxIdleTimeMinutes: action.payload.auth?.maxIdleTimeMinutes ? parseInt(action.payload.auth.maxIdleTimeMinutes, 10) : null,
      instanceCode: action.payload.instanceCode,
      user: null,
      userId: action.payload.auth?.userId,
      userName: action.payload.auth?.userName
    });
  }

  @Action(Logout)
  logout(ctx: StateContext<AuthStateModel>, action: Logout) {
    ctx.patchState({
      auth: null,
      authLicenseUserData: null,
      autoLoginAttempted: false, // The user logged out so reset this flag
      daysUntilPasswordExpires: null,
      maxIdleTimeMinutes: null,
      instanceCode: null,
      user: null,
      userId: null,
      userName: null
    });
  }

  @Action(SwitchCentralInstance)
  switchInstance(ctx: StateContext<AuthStateModel>, action: SwitchCentralInstance) {
    // Patch the state the same way as the Logout action because we are essentially logging out of the current instance
    ctx.patchState({
      auth: null,
      authLicenseUserData: null,
      autoLoginAttempted: false, // The user logged out so reset this flag
      daysUntilPasswordExpires: null,
      maxIdleTimeMinutes: null,
      instanceCode: action.payload.instanceCode,
      user: null,
      userId: null,
      userName: null
    });
  }

  @Action(SetUser)
  setUser(ctx: StateContext<AuthStateModel>, action: SetUser) {
    ctx.patchState({
      user: action.payload.user,
      userId: action.payload.user?.Id,
      userName: action.payload.user?.UserName,
    });
  }

  @Action(SetAuthLicenseUserData)
  setAuthLicenseUserData(ctx: StateContext<AuthStateModel>, action: SetAuthLicenseUserData) {
    ctx.patchState({
      authLicenseUserData: action.payload.authLicenseUserData,
      user: action.payload.authLicenseUserData.LicenseUser?.User,
      userId: action.payload.authLicenseUserData.LicenseUser?.User?.Id,
      userName: action.payload.authLicenseUserData.LicenseUser?.User?.UserName,
    });
  }

  @Action(SetAutoLoginAttempted)
  setAutoLoginAttempted(ctx: StateContext<AuthStateModel>, action: SetAutoLoginAttempted) {
    ctx.patchState({
      autoLoginAttempted: action.payload.autoLoginAttempted
    });
  }

  @Action(SetInstanceCode)
  setInstanceCode(ctx: StateContext<AuthStateModel>, action: SetInstanceCode) {
    ctx.patchState({
      instanceCode: action.payload.instanceCode
    });
  }

  @Action(SetLastActivatedRoute)
  setLastActivatedRoute(ctx: StateContext<AuthStateModel>, action: SetLastActivatedRoute) {
    ctx.patchState({
      lastActivatedRoute: action.payload.lastActivatedRoute
    });
  }

  @Action(SetSubdomainLicense)
  setSubdomainLicense(ctx: StateContext<AuthStateModel>, action: SetSubdomainLicense) {
    ctx.patchState({
      subdomainLicense: action.payload.subdomainLicense
    });
  }
}

/* AuthDataService */
@Injectable({
  providedIn: 'root'
})
export class AuthDataService {
  constructor(private store: Store) { }

  /* Auth */
  getAuth(): Auth {
    return this.store.selectSnapshot(AuthState.auth);
  }

  getAuth$(): Observable<Auth> {
    return this.store.select(AuthState.auth);
  }

  /* Auth License User Data */
  getAuthLicenseUserData(): AuthLicenseUserData {
    return this.store.selectSnapshot(AuthState.authLicenseUserData);
  }

  getAuthLicenseUserData$(): Observable<AuthLicenseUserData> {
    return this.store.select(AuthState.authLicenseUserData);
  }

  setAuthLicenseUserData$(authLicenseUserData: AuthLicenseUserData): Observable<any> {
    return this.store.dispatch(new SetAuthLicenseUserData({ authLicenseUserData: authLicenseUserData }));
  }

  /* Auto Login Attempted */
  getAutoLoginAttempted(): boolean {
    return this.store.selectSnapshot(AuthState.autoLoginAttempted);
  }

  getAutoLoginAttempted$(): Observable<boolean> {
    return this.store.select(AuthState.autoLoginAttempted);
  }

  setAutoLoginAttempted$(autoLoginAttempted: boolean): Observable<any> {
    return this.store.dispatch(new SetAutoLoginAttempted({ autoLoginAttempted }));
  }

  /* Days Until Password Expires */
  getDaysUntilPasswordExpires(): number {
    return this.store.selectSnapshot(AuthState.daysUntilPasswordExpires);
  }

  getDaysUntilPasswordExpires$(): Observable<number> {
    return this.store.select(AuthState.daysUntilPasswordExpires);
  }

  /* Last Selected Route */
  getLastActivatedRoute(): string[] {
    return this.store.selectSnapshot(AuthState.lastActivatedRoute);
  }

  getLastActivatedRoute$(): Observable<string[]> {
    return this.store.select(AuthState.lastActivatedRoute);
  }

  setLastActivatedRoute$(lastActivatedRoute: string[]): Observable<any> {
    return this.store.dispatch(new SetLastActivatedRoute({ lastActivatedRoute }));
  }

  /* Max Idle Time Minutes */
  getMaxIdleTimeMinutes(): number {
    return this.store.selectSnapshot(AuthState.maxIdleTimeMinutes);
  }

  getMaxIdleTimeMinutes$(): Observable<number> {
    return this.store.select(AuthState.maxIdleTimeMinutes);
  }

  /* Instance Code */
  getInstanceCode(): string {
    return this.store.selectSnapshot(AuthState.instanceCode);
  }

  getInstanceCode$(): Observable<string> {
    return this.store.select(AuthState.instanceCode);
  }

  setInstanceCode$(instanceCode: string): Observable<any> {
    return this.store.dispatch(new SetInstanceCode({ instanceCode }));
  }

  /* Subdomain License */
  getSubdomainLicense(): SubdomainLicense {
    return this.store.selectSnapshot(AuthState.subdomainLicense);
  }

  getSubdomainLicense$(): Observable<SubdomainLicense> {
    return this.store.select(AuthState.subdomainLicense);
  }

  setSubdomainLicense$(subdomainLicense: SubdomainLicense): Observable<any> {
    return this.store.dispatch(new SetSubdomainLicense({ subdomainLicense }));
  }

  /* User */
  getUser(): User {
    return this.store.selectSnapshot(AuthState.user);
  }

  getUser$(): Observable<User> {
    return this.store.select(AuthState.user);
  }

  setUser$(user: User): Observable<any> {
    return this.store.dispatch(new SetUser({ user: user }));
  }

  /* User Id */
  getUserId(): string {
    return this.store.selectSnapshot(AuthState.userId);
  }

  getUserId$(): Observable<string> {
    return this.store.select(AuthState.userId);
  }

  /* User Name */
  getUserName(): string {
    return this.store.selectSnapshot(AuthState.userName);
  }

  getUserName$(): Observable<string> {
    return this.store.select(AuthState.userName);
  }

  /* Login */
  login$(auth: Auth, instanceCode: string): Observable<any> {
    return this.store.dispatch(new Login({ auth, instanceCode }));
  }

  /* Logout */
  logout$(reason: LogoutReason, instanceCode: string): Observable<any> {
    return this.store.dispatch(new Logout({ reason, instanceCode }));
  }

  /* Switch Instance */
  switchCentralInstance$(instanceCode: string): Observable<any> {
    return this.store.dispatch(new SwitchCentralInstance({ instanceCode }));
  }
}
